import type { FeedList, FeedProviderId, FeedRun, FeedRunResult, Page } from '@fruuit/shared'
import { api } from '../../api/client'
import { useApiMutation, useApiQuery } from '../../api/hooks'

/** Query keys: everything under `['feeds']` is refreshed after a run or acknowledgement. */
export const feedKeys = {
  all: ['feeds'] as const,
  list: () => ['feeds', 'list'] as const,
  runs: (provider: string, status: string, cursor: string | null) =>
    ['feeds', 'runs', provider, status, cursor] as const,
}

/** `GET /feeds`: provider status cards. */
export function useFeeds() {
  return useApiQuery<FeedList>(feedKeys.list(), '/feeds')
}

/** `GET /feeds/:provider/runs`: paginated run history, newest first. */
export function useFeedRuns(provider: FeedProviderId, status: string, cursor: string | null) {
  return useApiQuery<Page<FeedRun>>(
    feedKeys.runs(provider, status, cursor),
    `/feeds/${encodeURIComponent(provider)}/runs`,
    {
      query: { limit: 25, cursor: cursor ?? undefined, status: status || undefined },
      placeholderData: (previous) => previous,
    },
  )
}

/** `POST /feeds/:provider/run`: manual re-run (operators only, ⚙ P-ADM-011). */
export function useRunFeed(provider: FeedProviderId) {
  return useApiMutation(
    (_: undefined, { requestId }) =>
      api.post<FeedRunResult>(
        `/feeds/${encodeURIComponent(provider)}/run`,
        {},
        {
          requestId,
        },
      ),
    { invalidate: [feedKeys.all, ['alerts'], ['admin', 'overview']] },
  )
}
